import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";

const ViewTicketModal = ({ isOpen, onClose, ticket }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    // Prevent background scrolling
    useEffect(() => {
        if (isOpen) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "unset";
        return () => (document.body.style.overflow = "unset");
    }, [isOpen]);

    if (!isOpen || !ticket) return null;

    const handleDownload = () => {
        setIsDownloading(true);
        setTimeout(() => {
            setIsDownloading(false);
            onClose();
            Swal.fire({
                icon: "success",
                title: "Ticket Saved",
                text: "E-ticket saved successfully.",
                confirmButtonColor: "#0B1F33",
                timer: 1500,
                showConfirmButton: false,
            });
        }, 1500);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 font-sans">

            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-[#0B1F33]/80 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Styles */}
            <style>{`
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            {/* Compact Modal Card */}
            <div className="relative w-full max-w-sm bg-white rounded-[2rem] shadow-2xl overflow-hidden animate-[slideUp_0.3s_ease-out] max-h-[90vh] flex flex-col">

                {/* --- COMPACT HEADER --- */}
                <div className="bg-[#0B1F33] p-5 pb-8 relative text-white text-center flex-shrink-0">
                    {/* Decor */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-[40px] translate-x-1/2 -translate-y-1/2"></div>

                    {/* Close */}
                    <button onClick={onClose} className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors z-20">
                        <span className="material-symbols-outlined text-xs">close</span>
                    </button>

                    {/* Badge */}
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#0B1F33] text-[9px] font-black uppercase tracking-widest mb-3 shadow-lg">
                        <span className="material-symbols-outlined text-[10px]">verified</span> Verified Pass
                    </div>

                    <h2 className="text-xl font-black leading-tight mb-1">{ticket.title}</h2>
                    <p className="text-gray-400 text-xs font-medium">{ticket.type || "General Entry"}</p>
                </div>

                {/* --- PERFORATION --- */}
                <div className="relative h-6 bg-white -mt-3 z-10 flex-shrink-0">
                    <div className="absolute -top-3 -left-3 w-6 h-6 bg-[#0B1F33] rounded-full"></div>
                    <div className="absolute -top-3 -right-3 w-6 h-6 bg-[#0B1F33] rounded-full"></div>
                    <div className="absolute top-1/2 left-5 right-5 border-t-2 border-dashed border-gray-200"></div>
                </div>

                {/* --- COMPACT BODY --- */}
                <div className="px-6 pb-6 pt-0 overflow-y-auto hide-scrollbar">

                    {/* Info Grid (4 Cols) */}
                    <div className="grid grid-cols-2 gap-x-2 gap-y-4 text-center border-b border-gray-100 pb-4 mb-4">
                        <div>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-0.5">Date</p>
                            <p className="text-sm font-black text-[#0B1F33]">{ticket.date.split('•')[0]}</p>
                        </div>
                        <div>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-0.5">Time</p>
                            <p className="text-sm font-black text-[#0B1F33]">{ticket.date.split('•')[1] || "07:00 PM"}</p>
                        </div>
                        <div className="col-span-2">
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-0.5">Venue</p>
                            <p className="text-xs font-bold text-[#0B1F33] truncate px-2">{ticket.venue.split(',')[0]}</p>
                        </div>
                    </div>

                    {/* Secondary Details (Row) */}
                    <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3 mb-4 border border-gray-100">
                        <div className="text-center">
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Gate</p>
                            <p className="text-xs font-black text-[#0B1F33]">{ticket.gate || "A"}</p>
                        </div>
                        <div className="w-px h-6 bg-gray-200"></div>
                        <div className="text-center">
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Seat</p>
                            <p className="text-xs font-black text-[#0B1F33]">{ticket.seat || "GA"}</p>
                        </div>
                        <div className="w-px h-6 bg-gray-200"></div>
                        <div className="text-center">
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Org</p>
                            <p className="text-xs font-black text-[#0B1F33]">Eventix</p>
                        </div>
                    </div>

                    {/* QR Code (Optimized Size) */}
                    <div className="flex flex-col items-center justify-center mb-4">
                        <div className="bg-white p-1.5 rounded-2xl border-2 border-[#0B1F33] shadow-lg">
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=EVENTIX-${ticket.id}`}
                                alt="QR"
                                className="w-[140px] h-[140px] object-contain"
                            />
                        </div>
                        <p className="text-[9px] font-mono text-gray-400 mt-2 tracking-widest">
                            ID: {ticket.id}-8X92
                        </p>
                    </div>

                    {/* Download Button */}
                    <button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className={`
                            w-full py-3 rounded-xl font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-md transition-all
                            ${isDownloading ? "bg-gray-100 text-gray-400" : "bg-[#0B1F33] text-white hover:bg-[#D4AF37] hover:text-[#0B1F33]"}
                        `}
                    >
                        {isDownloading ? (
                            <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-base">download</span>
                                Save Ticket
                            </>
                        )}
                    </button>
                </div>

                {/* Footer Decor */}
                <div className="h-2 w-full bg-[repeating-linear-gradient(45deg,#0B1F33,#0B1F33_5px,#D4AF37_5px,#D4AF37_10px)] opacity-20 flex-shrink-0"></div>

            </div>
        </div>
    );
};

export default ViewTicketModal;